import { useState, useMemo } from 'react';
import type { ApiEndpoints, SBStatusEvent } from '@avisoma/shared';
import { Card } from '../../components/Card';
import { DescriptionList, DescriptionTerm, DescriptionDetails } from '../../catalyst/description-list';
import { SBTable } from '../../components/reconstructedDocument/extractionTables/SBTable';
import { ExtractionTableCard } from '../../components/reconstructedDocument/extractionTables/ExtractionTableCard';
import { RowInspectorDialog } from '../../components/reconstructedDocument/extractionTables/RowInspectorDialog';
import { SourceCard } from './SourceCard';
import { StatusPackWarnings } from './StatusPackWarnings';

interface SBEventsProps {
  response: ApiEndpoints<string>['/api/projects/:id']['GET']['response'];
}

export function SBEvents({ response }: SBEventsProps) {
  const data = response.item.data?.sbEvents;
  const sbs = data?.sbs;
  const [inspected, setInspected] = useState<SBStatusEvent | null>(null);

  const pageIdToPosition = useMemo(() => {
    const map = new Map<string, number>();
    data?.pageIds.forEach((pageId, index) => map.set(pageId, index + 1));
    return map;
  }, [data?.pageIds]);

  if (!data || !sbs) {
    return null;
  }

  return (
    <div className="space-y-6">
      <SourceCard sources={[{ documentId: data.documentId, pageIds: data.pageIds }]}>
        <StatusPackWarnings warnings={data.warnings} />
      </SourceCard>

      <Card header={<h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">SB Status Events</h2>}>
        <DescriptionList>
          <DescriptionTerm>SB Count</DescriptionTerm>
          <DescriptionDetails>{sbs.length}</DescriptionDetails>
        </DescriptionList>
      </Card>

      <ExtractionTableCard label="SB Status Events" rows={sbs} csvTitle="SB Status Events" bodyHeight="project">
        <SBTable
          rows={sbs}
          pageIdToPosition={pageIdToPosition}
          onInspect={setInspected}
          supersededRevisions={data.supersededRevisions}
        />
      </ExtractionTableCard>

      {data.supersededRevisions?.length ? (
        <ExtractionTableCard
          label="SB Revision History (superseded)"
          rows={data.supersededRevisions}
          csvTitle="sb-revision-history"
          bodyHeight="project"
        >
          <SBTable
            rows={data.supersededRevisions}
            pageIdToPosition={pageIdToPosition}
            onInspect={setInspected}
            showSupersededBy
          />
        </ExtractionTableCard>
      ) : null}

      <RowInspectorDialog
        open={inspected != null}
        onClose={() => setInspected(null)}
        title="SB Status row"
        data={inspected}
      />
    </div>
  );
}
